import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  Search, Plus, Phone, Mail, ChevronRight, Home, Building, Building2,
  Edit2, Calendar, MessageSquare, MoreHorizontal, MapPin, Clock,
  ChevronUp, ChevronDown, Filter, Download, RefreshCw, X, Check,
  AlertCircle, TrendingUp, Users, GitMerge, AlertTriangle, Trash2, Lock
} from 'lucide-react';
import { formatFullName, formatPhone } from '../utils/helpers';
import { clientTypes, frequencyOptions } from '../utils/sampleData';
import { format, parseISO, isPast, isFuture, differenceInDays } from 'date-fns';
import Modal from '../components/Modal';
import ClientForm from '../components/ClientForm';
import './ClientsV2.css';

function Clients() {
  const navigate = useNavigate();
  const { state, getClientProperties, addClient, updateClient, mergeClients, findDuplicateClients, deleteClient, verifyMasterPassword, hasMasterPassword } = useApp();
  const { clients, jobs, properties, invoices } = state;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFrequency, setFilterFrequency] = useState('all');
  const [filterMissingInfo, setFilterMissingInfo] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Merge mode state
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [primaryClientId, setPrimaryClientId] = useState(null);
  const [merging, setMerging] = useState(false);

  // Delete client state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [masterPassword, setMasterPassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeDropdown && !e.target.closest('.actions-dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  // Potential duplicates detection
  const potentialDuplicates = useMemo(() => {
    const duplicateSets = [];
    const processed = new Set();
    
    clients.forEach(client => {
      if (processed.has(client.id)) return;
      
      const dupes = findDuplicateClients(client, client.id);
      if (dupes.length > 0) {
        const allInSet = [client, ...dupes];
        // Only add if we haven't processed any of these clients yet
        if (!allInSet.some(c => processed.has(c.id))) {
          duplicateSets.push(allInSet);
          allInSet.forEach(c => processed.add(c.id));
        }
      }
    });
    
    return duplicateSets;
  }, [clients, findDuplicateClients]);

  // Enhanced client data with job counts and property info
  const enrichedClients = useMemo(() => {
    return clients.map(client => {
      const clientJobs = jobs.filter(j => j.clientId === client.id);
      const upcomingJobs = clientJobs.filter(j => 
        j.status === 'scheduled' && j.date && isFuture(parseISO(j.date))
      );
      const pastJobs = clientJobs.filter(j => 
        j.status === 'completed' || (j.date && isPast(parseISO(j.date)))
      );
      const clientProperties = properties.filter(p => p.clientId === client.id);
      const primaryProperty = clientProperties.find(p => p.isPrimary) || clientProperties[0];
      
      // Calculate total revenue
      const totalRevenue = pastJobs
        .filter(j => j.status === 'completed')
        .reduce((sum, j) => sum + (j.price || 0), 0);
      
      // Get next scheduled job
      const nextJob = upcomingJobs.sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      )[0];

      // --- Health Score Calculation ---
      const completedJobs = clientJobs.filter(j => j.status === 'completed');
      const cancelledJobs = clientJobs.filter(j => j.status === 'cancelled');
      const jobsWithLabor = completedJobs.filter(j => j.laborCost != null && j.laborCost > 0);
      const avgLaborPercent = jobsWithLabor.length > 0
        ? Math.round(jobsWithLabor.reduce((sum, j) => sum + (j.laborPercent || 0), 0) / jobsWithLabor.length)
        : null;

      const clientInvoices = invoices.filter(i => i.clientId === client.id);
      const paidInvoices = clientInvoices.filter(i => i.status === 'paid');
      const unpaidInvoices = clientInvoices.filter(i => i.status === 'sent' || i.status === 'overdue');
      const totalPaid = paidInvoices.reduce((sum, i) => sum + (i.total || 0), 0);
      const totalOutstanding = unpaidInvoices.reduce((sum, i) => sum + (i.total || 0), 0);

      let healthScore = 70;
      if (avgLaborPercent != null) {
        if (avgLaborPercent <= 40) healthScore += 15;
        else if (avgLaborPercent <= 50) healthScore += 5;
        else healthScore -= 10;
      }
      if (totalOutstanding === 0) healthScore += 10;
      else if (totalPaid > 0 && totalOutstanding > totalPaid * 0.5) healthScore -= 15;
      if (completedJobs.length > 0 && cancelledJobs.length > completedJobs.length * 0.1) healthScore -= 10;
      healthScore = Math.max(0, Math.min(100, healthScore));

      return {
        ...client,
        upcomingJobCount: upcomingJobs.length,
        pastJobCount: pastJobs.length,
        totalRevenue,
        nextJob,
        healthScore,
        avgLaborPercent,
        totalOutstanding,
        primaryProperty,
        propertyCount: clientProperties.length,
        fullAddress: primaryProperty 
          ? `${primaryProperty.addressLine1}, ${primaryProperty.city}` 
          : ''
      };
    });
  }, [clients, jobs, properties, invoices]);

  // Filter and search
  const filteredClients = useMemo(() => {
    let result = enrichedClients;
    
    // Status filter
    if (filterStatus !== 'all') {
      result = result.filter(c => c.status === filterStatus);
    }
    
    // Type filter
    if (filterType !== 'all') {
      result = result.filter(c => c.clientType === filterType);
    }
    
    // Frequency filter
    if (filterFrequency !== 'all') {
      result = result.filter(c => c.frequency === filterFrequency);
    }
    
    // Missing info filter
    if (filterMissingInfo !== 'all') {
      switch (filterMissingInfo) {
        case 'no_email':
          result = result.filter(c => !c.email || c.email.trim() === '');
          break;
        case 'no_phone':
          result = result.filter(c => !c.phone || c.phone.trim() === '');
          break;
        case 'no_address':
          result = result.filter(c => !c.primaryProperty || !c.primaryProperty.addressLine1);
          break;
        case 'no_contact':
          result = result.filter(c => 
            (!c.email || c.email.trim() === '') && 
            (!c.phone || c.phone.trim() === '')
          );
          break;
        default:
          break;
      }
    }
    
    // Search - now includes address and phone
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c => 
        formatFullName(c).toLowerCase().includes(term) ||
        c.phone?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.fullAddress?.toLowerCase().includes(term) ||
        c.primaryProperty?.addressLine1?.toLowerCase().includes(term) ||
        c.primaryProperty?.city?.toLowerCase().includes(term) ||
        c.primaryProperty?.zipCode?.toLowerCase().includes(term)
      );
    }
    
    // Sorting
    result = [...result].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'name':
          aVal = formatFullName(a).toLowerCase();
          bVal = formatFullName(b).toLowerCase();
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'frequency':
          aVal = a.frequency || 'zzz';
          bVal = b.frequency || 'zzz';
          break;
        case 'upcoming':
          aVal = a.upcomingJobCount;
          bVal = b.upcomingJobCount;
          break;
        case 'past':
          aVal = a.pastJobCount;
          bVal = b.pastJobCount;
          break;
        case 'revenue':
          aVal = a.totalRevenue;
          bVal = b.totalRevenue;
          break;
        case 'health':
          aVal = a.healthScore;
          bVal = b.healthScore;
          break;
        case 'nextJob':
          aVal = a.nextJob?.date || '9999-12-31';
          bVal = b.nextJob?.date || '9999-12-31';
          break;
        case 'created':
          aVal = a.createdAt || '1970-01-01';
          bVal = b.createdAt || '1970-01-01';
          break;
        default:
          aVal = formatFullName(a).toLowerCase();
          bVal = formatFullName(b).toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [enrichedClients, searchTerm, filterType, filterStatus, filterFrequency, sortField, sortDirection]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort indicator
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' 
      ? <ChevronUp size={14} className="sort-icon" />
      : <ChevronDown size={14} className="sort-icon" />;
  };

  const handleAddClient = () => {
    setEditingClient(null);
    setShowModal(true);
  };

  const handleEditClient = (e, client) => {
    e.stopPropagation();
    setEditingClient(client);
    setShowModal(true);
    setActiveDropdown(null);
  };

  const handleBookClient = (e, client) => {
    e.stopPropagation();
    navigate('/admin/dispatch', { state: { clientId: client.id, action: 'book' } });
    setActiveDropdown(null);
  };

  const handleMessageClient = (e, client) => {
    e.stopPropagation();
    navigate('/messages', { state: { clientId: client.id, action: 'new' } });
    setActiveDropdown(null);
  };

  const handleDeleteClient = async (e, client) => {
    e.stopPropagation();
    setActiveDropdown(null);
    const hasPassword = await hasMasterPassword();
    if (!hasPassword) {
      setDeleteError('Master password has not been set. Please set it in Settings first.');
      setClientToDelete(client);
      setShowDeleteModal(true);
      return;
    }
    setClientToDelete(client);
    setMasterPassword('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!masterPassword.trim()) {
      setDeleteError('Please enter the master password.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    const result = await verifyMasterPassword(masterPassword);
    if (!result.valid) {
      setDeleteError(result.notSet ? 'Master password has not been set. Please set it in Settings first.' : 'Incorrect master password.');
      setDeleting(false);
      return;
    }
    await deleteClient(clientToDelete.id);
    setShowDeleteModal(false);
    setClientToDelete(null);
    setMasterPassword('');
    setDeleting(false);
  };

  const handleSaveClient = async (clientData) => {
    if (editingClient) {
      await updateClient(editingClient.id, clientData);
    } else {
      await addClient(clientData);
    }
    setShowModal(false);
    setEditingClient(null);
  };

  // Merge Mode Functions
  const toggleMergeMode = () => {
    setMergeMode(!mergeMode);
    setSelectedForMerge([]);
    setPrimaryClientId(null);
  };

  const toggleClientForMerge = (clientId) => {
    setSelectedForMerge(prev => {
      if (prev.includes(clientId)) {
        // If removing the primary, reset primary selection
        if (primaryClientId === clientId) {
          setPrimaryClientId(null);
        }
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleStartMerge = () => {
    if (selectedForMerge.length < 2) {
      alert('Please select at least 2 clients to merge');
      return;
    }
    // Default to first selected as primary
    setPrimaryClientId(selectedForMerge[0]);
    setShowMergeModal(true);
  };

  const handleConfirmMerge = async () => {
    if (!primaryClientId || selectedForMerge.length < 2) return;
    
    setMerging(true);
    try {
      // Get all secondary client IDs (not the primary)
      const secondaryIds = selectedForMerge.filter(id => id !== primaryClientId);
      
      // Merge each secondary into primary
      for (const secondaryId of secondaryIds) {
        await mergeClients(primaryClientId, secondaryId);
      }
      
      alert(`✅ Successfully merged ${selectedForMerge.length} clients`);
      setShowMergeModal(false);
      setMergeMode(false);
      setSelectedForMerge([]);
      setPrimaryClientId(null);
    } catch (error) {
      console.error('Merge failed:', error);
      alert('Failed to merge clients. Please try again.');
    } finally {
      setMerging(false);
    }
  };

  const getClientIcon = (type) => {
    switch (type) {
      case 'residential': return Home;
      case 'property_manager': return Building;
      case 'commercial': return Building2;
      default: return Home;
    }
  };

  const getFrequencyLabel = (freq) => {
    const option = frequencyOptions.find(f => f.value === freq);
    return option ? option.label : freq || '—';
  };

  const getFrequencyClass = (freq) => {
    if (!freq) return '';
    if (freq === 'weekly') return 'freq-weekly';
    if (freq === 'biweekly') return 'freq-biweekly';
    if (freq === 'monthly') return 'freq-monthly';
    return 'freq-other';
  };

  // Stats
  const stats = useMemo(() => ({
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    leads: clients.filter(c => c.status === 'lead').length,
    needsAttention: enrichedClients.filter(c => 
      c.status === 'active' && c.upcomingJobCount === 0
    ).length,
    missingEmail: clients.filter(c => !c.email || c.email.trim() === '').length,
    missingPhone: clients.filter(c => !c.phone || c.phone.trim() === ''),
    duplicateSets: potentialDuplicates.length
  }), [clients, enrichedClients, potentialDuplicates]);

  const clearFilters = () => {
    setFilterType('all');
    setFilterStatus('all');
    setFilterFrequency('all');
    setFilterMissingInfo('all');
    setSearchTerm('');
  };

  const hasActiveFilters = filterType !== 'all' || filterStatus !== 'all' || filterFrequency !== 'all' || filterMissingInfo !== 'all' || searchTerm;

  return (
    <div className="clients-page-v2">
      {/* Merge Mode Banner */}
      {mergeMode && (
        <div className="merge-mode-banner">
          <div className="merge-info">
            <GitMerge size={20} />
            <span>Merge Mode: Select clients to merge ({selectedForMerge.length} selected)</span>
          </div>
          <div className="merge-actions">
            {selectedForMerge.length >= 2 && (
              <button className="btn btn-primary btn-sm" onClick={handleStartMerge}>
                <Check size={16} />
                Merge Selected
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={toggleMergeMode}>
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Duplicates Warning Banner */}
      {!mergeMode && potentialDuplicates.length > 0 && (
        <div className="duplicates-warning-banner">
          <div className="warning-content">
            <AlertTriangle size={18} />
            <span>
              <strong>{potentialDuplicates.length} potential duplicate{potentialDuplicates.length > 1 ? 's' : ''}</strong> detected
            </span>
          </div>
          <button className="btn btn-warning btn-sm" onClick={toggleMergeMode}>
            <GitMerge size={16} />
            Review & Merge
          </button>
        </div>
      )}

      {/* Header */}
      <div className="page-header-v2">
        <div className="header-left">
          <h1>Clients</h1>
          <div className="header-stats">
            <span className="stat-pill">{stats.total} total</span>
            <span className="stat-pill active">{stats.active} active</span>
            <span className="stat-pill lead">{stats.leads} leads</span>
            {stats.needsAttention > 0 && (
              <span className="stat-pill warning">
                <AlertCircle size={12} />
                {stats.needsAttention} need jobs
              </span>
            )}
            {stats.missingEmail > 0 && (
              <span 
                className="stat-pill warning clickable" 
                onClick={() => {
                  setFilterMissingInfo('no_email');
                  setShowFilters(true);
                }}
                title="Click to filter"
              >
                <Mail size={12} />
                {stats.missingEmail} no email
              </span>
            )}
          </div>
        </div>
        <div className="header-actions">
          {!mergeMode && (
            <button className="btn btn-secondary" onClick={toggleMergeMode}>
              <GitMerge size={18} />
              Merge Clients
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={18} />
            Filters
            {hasActiveFilters && <span className="filter-badge" />}
          </button>
          <button className="btn btn-primary" onClick={handleAddClient}>
            <Plus size={18} />
            Add Client
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-bar-v2">
        <div className="search-input-wrapper">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, phone, email, or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <span className="search-results-count">
          {filteredClients.length} {filteredClients.length === 1 ? 'result' : 'results'}
        </span>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Type</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="residential">Residential</option>
              <option value="property_manager">Property Manager</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="lead">Lead</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Frequency</label>
            <select value={filterFrequency} onChange={(e) => setFilterFrequency(e.target.value)}>
              <option value="all">All Frequencies</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="one_time">One-Time</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Missing Info</label>
            <select value={filterMissingInfo} onChange={(e) => setFilterMissingInfo(e.target.value)}>
              <option value="all">All Clients</option>
              <option value="no_email">⚠️ Missing Email</option>
              <option value="no_phone">⚠️ Missing Phone</option>
              <option value="no_address">⚠️ Missing Address</option>
              <option value="no_contact">⚠️ No Contact Info</option>
            </select>
          </div>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              <X size={14} />
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="clients-table-container">
        <table className="clients-table">
          <thead>
            <tr>
              {mergeMode && <th className="checkbox-col"></th>}
              <th className="sortable" onClick={() => handleSort('name')}>
                Client <SortIndicator field="name" />
              </th>
              <th>Contact</th>
              <th>Address</th>
              <th className="sortable" onClick={() => handleSort('frequency')}>
                Frequency <SortIndicator field="frequency" />
              </th>
              <th className="sortable center" onClick={() => handleSort('upcoming')}>
                Upcoming <SortIndicator field="upcoming" />
              </th>
              <th className="sortable center" onClick={() => handleSort('past')}>
                Past <SortIndicator field="past" />
              </th>
              <th className="sortable" onClick={() => handleSort('nextJob')}>
                Next Job <SortIndicator field="nextJob" />
              </th>
              <th className="sortable" onClick={() => handleSort('status')}>
                Status <SortIndicator field="status" />
              </th>
              <th className="sortable center" onClick={() => handleSort('health')}>
                Health <SortIndicator field="health" />
              </th>
              <th className="sortable" onClick={() => handleSort('created')}>
                Created <SortIndicator field="created" />
              </th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr className="empty-row">
                <td colSpan={mergeMode ? "12" : "11"}>
                  <div className="empty-state-v2">
                    <Users size={48} />
                    <h3>No clients found</h3>
                    <p>Try adjusting your search or filters</p>
                    {hasActiveFilters && (
                      <button className="btn btn-secondary" onClick={clearFilters}>
                        Clear Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filteredClients.map(client => {
                const Icon = getClientIcon(client.clientType);
                const typeConfig = clientTypes[client.clientType];
                const isSelected = selectedForMerge.includes(client.id);
                
                return (
                  <tr 
                    key={client.id} 
                    className={`client-row ${client.status} ${isSelected ? 'selected-for-merge' : ''}`}
                    onClick={() => mergeMode ? toggleClientForMerge(client.id) : navigate(`/admin/clients/${client.id}`)}
                  >
                    {/* Checkbox for merge mode */}
                    {mergeMode && (
                      <td className="checkbox-cell" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleClientForMerge(client.id)}
                        />
                      </td>
                    )}
                    {/* Client Name & Type */}
                    <td className="client-cell">
                      <div className="client-avatar-sm">
                        <Icon size={16} />
                      </div>
                      <div className="client-name-col">
                        <span className="client-name-text">{formatFullName(client)}</span>
                        <span className="client-type-text">{typeConfig?.label}</span>
                      </div>
                    </td>

                    {/* Contact */}
                    <td className="contact-cell">
                      <div className="contact-lines">
                        <span className="contact-line">
                          <Phone size={12} />
                          {formatPhone(client.phone)}
                        </span>
                        {client.email && (
                          <span className="contact-line email">
                            <Mail size={12} />
                            {client.email}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Address */}
                    <td className="address-cell">
                      {client.primaryProperty ? (
                        <div className="address-content">
                          <span className="address-line">{client.primaryProperty.addressLine1}</span>
                          <span className="address-city">
                            {client.primaryProperty.city}, {client.primaryProperty.state}
                          </span>
                        </div>
                      ) : (
                        <span className="no-address">No address</span>
                      )}
                    </td>

                    {/* Frequency */}
                    <td className="frequency-cell">
                      <span className={`frequency-badge ${getFrequencyClass(client.frequency)}`}>
                        {getFrequencyLabel(client.frequency)}
                      </span>
                    </td>

                    {/* Upcoming Jobs */}
                    <td className="jobs-cell center">
                      <span className={`job-count ${client.upcomingJobCount === 0 && client.status === 'active' ? 'warning' : ''}`}>
                        {client.upcomingJobCount}
                      </span>
                    </td>

                    {/* Past Jobs */}
                    <td className="jobs-cell center">
                      <span className="job-count past">{client.pastJobCount}</span>
                    </td>

                    {/* Next Job */}
                    <td className="next-job-cell">
                      {client.nextJob ? (
                        <div className="next-job-info">
                          <span className="next-job-date">
                            {format(parseISO(client.nextJob.date), 'MMM d')}
                          </span>
                          <span className="next-job-time">
                            {client.nextJob.scheduledTime || '—'}
                          </span>
                        </div>
                      ) : (
                        <span className={`no-job ${client.status === 'active' ? 'warning' : ''}`}>
                          {client.status === 'active' ? 'None scheduled' : '—'}
                        </span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="status-cell">
                      <span className={`status-badge-v2 ${client.status}`}>
                        {client.status}
                      </span>
                    </td>

                    {/* Health Score */}
                    <td className="health-cell center">
                      <div className={`health-score-badge ${client.healthScore >= 80 ? 'excellent' : client.healthScore >= 60 ? 'good' : client.healthScore >= 40 ? 'fair' : 'poor'}`}>
                        <div className="health-bar">
                          <div className="health-fill" style={{ width: `${client.healthScore}%` }}></div>
                        </div>
                        <span className="health-number">{client.healthScore}</span>
                      </div>
                    </td>

                    {/* Created Date */}
                    <td className="created-cell">
                      {client.createdAt ? (
                        <span className="created-date">
                          {format(parseISO(client.createdAt), 'MMM d, yyyy')}
                        </span>
                      ) : (
                        <span className="no-date">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                      <div className="actions-dropdown-container">
                        <button 
                          className="actions-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(activeDropdown === client.id ? null : client.id);
                          }}
                        >
                          <MoreHorizontal size={18} />
                        </button>
                        {activeDropdown === client.id && (
                          <div className="actions-dropdown">
                            <button onClick={(e) => { handleEditClient(e, client); setActiveDropdown(null); }}>
                              <Edit2 size={14} /> Edit Client
                            </button>
                            <button onClick={(e) => { handleBookClient(e, client); setActiveDropdown(null); }}>
                              <Calendar size={14} /> Book Appointment
                            </button>
                            <button onClick={(e) => { handleMessageClient(e, client); setActiveDropdown(null); }}>
                              <MessageSquare size={14} /> Send Message
                            </button>
                            <Link
                              to={`/admin/clients/${client.id}`}
                              onClick={() => setActiveDropdown(null)}
                            >
                              <ChevronRight size={14} /> View Details
                            </Link>
                            <div className="dropdown-divider"></div>
                            <button className="delete-action" onClick={(e) => handleDeleteClient(e, client)}>
                              <Trash2 size={14} /> Delete Client
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Client Form Modal */}
      {showModal && (
        <Modal
          isOpen={showModal}
          title={editingClient ? 'Edit Client' : 'Add New Client'}
          onClose={() => { setShowModal(false); setEditingClient(null); }}
        >
          <ClientForm
            client={editingClient}
            onSave={handleSaveClient}
            onCancel={() => { setShowModal(false); setEditingClient(null); }}
          />
        </Modal>
      )}

      {/* Merge Clients Modal */}
      {showMergeModal && (
        <Modal
          isOpen={showMergeModal}
          title="Merge Clients"
          onClose={() => {
            setShowMergeModal(false);
            setPrimaryClientId(null);
          }}
        >
          <div className="merge-modal-content">
            <div className="merge-warning">
              <AlertTriangle size={20} />
              <p>
                <strong>This action cannot be undone.</strong> All jobs, properties, invoices, and messages 
                from the secondary clients will be moved to the primary client.
              </p>
            </div>

            <div className="merge-selection">
              <h4>Select Primary Client (will be kept):</h4>
              <div className="merge-client-options">
                {selectedForMerge.map(clientId => {
                  const client = enrichedClients.find(c => c.id === clientId);
                  if (!client) return null;
                  
                  return (
                    <div 
                      key={clientId}
                      className={`merge-client-option ${primaryClientId === clientId ? 'primary' : ''}`}
                      onClick={() => setPrimaryClientId(clientId)}
                    >
                      <div className="option-radio">
                        {primaryClientId === clientId ? (
                          <Check size={16} className="check-icon" />
                        ) : (
                          <div className="radio-circle" />
                        )}
                      </div>
                      <div className="option-info">
                        <strong>{formatFullName(client)}</strong>
                        <div className="option-details">
                          {client.email && <span>{client.email}</span>}
                          {client.phone && <span>{client.phone}</span>}
                          <span>{client.pastJobCount} past jobs</span>
                          <span>{client.propertyCount} properties</span>
                        </div>
                      </div>
                      {primaryClientId === clientId && (
                        <span className="primary-badge">PRIMARY</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="merge-summary">
              {primaryClientId && (
                <p>
                  The following clients will be merged into <strong>
                    {formatFullName(enrichedClients.find(c => c.id === primaryClientId))}
                  </strong>:
                </p>
              )}
              <ul>
                {selectedForMerge.filter(id => id !== primaryClientId).map(clientId => {
                  const client = enrichedClients.find(c => c.id === clientId);
                  return client ? <li key={clientId}>{formatFullName(client)}</li> : null;
                })}
              </ul>
            </div>

            <div className="merge-modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowMergeModal(false);
                  setPrimaryClientId(null);
                }}
                disabled={merging}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleConfirmMerge}
                disabled={!primaryClientId || merging}
              >
                {merging ? (
                  <>
                    <RefreshCw size={16} className="spinning" />
                    Merging...
                  </>
                ) : (
                  <>
                    <GitMerge size={16} />
                    Confirm Merge
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Client Modal */}
      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          title="Delete Client"
          onClose={() => { setShowDeleteModal(false); setClientToDelete(null); setMasterPassword(''); setDeleteError(''); }}
        >
          <div className="delete-modal-content">
            <div className="merge-warning">
              <AlertTriangle size={20} />
              <p>
                <strong>This action cannot be undone.</strong> You are about to permanently delete
                {clientToDelete && <> <strong>{formatFullName(clientToDelete)}</strong></>}.
                All associated data will remain but will no longer be linked to this client.
              </p>
            </div>

            {deleteError && !deleteError.includes('not been set') && (
              <div className="delete-error">
                <Lock size={16} />
                {deleteError}
              </div>
            )}

            {deleteError && deleteError.includes('not been set') ? (
              <div className="delete-error">
                <Lock size={16} />
                {deleteError}
              </div>
            ) : (
              <div className="master-password-input">
                <label htmlFor="masterPassword">
                  <Lock size={16} />
                  Enter Master Password
                </label>
                <input
                  id="masterPassword"
                  type="password"
                  value={masterPassword}
                  onChange={(e) => setMasterPassword(e.target.value)}
                  placeholder="Enter master password to confirm"
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmDelete()}
                  autoFocus
                />
              </div>
            )}

            <div className="merge-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowDeleteModal(false); setClientToDelete(null); setMasterPassword(''); setDeleteError(''); }}
                disabled={deleting}
              >
                Cancel
              </button>
              {!deleteError?.includes('not been set') && (
                <button
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleting || !masterPassword.trim()}
                >
                  {deleting ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete Client
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Clients;
